import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, loadToken } from '../lib/api';
import { useMe } from '../lib/me';

type State = 'checking' | 'valid' | 'success' | 'invalid' | 'missing';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh: refreshMe } = useMe();
  const token = searchParams.get('token') ?? '';
  const [state, setState] = useState<State>('checking');
  const isAuthenticated = !!loadToken();

  useEffect(() => {
    if (!token) {
      setState('missing');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await api.validateEmailVerificationToken({ token });
        if (cancelled) return;
        await api.verifyEmail({ token });
        if (cancelled) return;
        // Refresh the cached /me so the dashboard banner disappears
        // without requiring a page reload.
        if (isAuthenticated) await refreshMe();
        if (cancelled) return;
        setState('success');
        setTimeout(() => navigate(isAuthenticated ? '/' : '/login'), 2500);
      } catch {
        if (!cancelled) setState('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate, isAuthenticated, refreshMe]);

  async function handleResend() {
    try {
      await api.resendVerificationEmail();
      setState('checking');
    } catch {
      // stays in error state
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold tracking-tight"><span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">QuickTL</span></h1>
          <p className="mt-1 text-sm text-slate-400">Email verification</p>
        </div>

        {state === 'checking' && (
          <div className="rounded-md bg-slate-900 border border-slate-800 px-4 py-6 text-center text-sm text-slate-400">
            Verifying your email…
          </div>
        )}

        {state === 'success' && (
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 px-4 py-4 text-sm text-emerald-300">
            Email verified successfully. Redirecting you…
          </div>
        )}

        {(state === 'missing' || state === 'invalid') && (
          <div className="space-y-4">
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-4 py-4 text-sm text-red-400">
              {state === 'missing'
                ? 'This page requires a verification token. Please use the link from your email.'
                : 'This verification link is invalid, has expired, or has already been used.'}
            </div>
            {isAuthenticated && (
              <button
                onClick={handleResend}
                className="block w-full text-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 transition-colors"
              >
                Send a new verification email
              </button>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-sm text-slate-400">
          <Link to={isAuthenticated ? '/' : '/login'} className="text-emerald-400 hover:text-emerald-300">
            {isAuthenticated ? 'Back to dashboard' : 'Back to sign in'}
          </Link>
        </p>
      </div>
    </div>
  );
}
